import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { A, pipe } from "@mobily/ts-belt"
import type { Group } from "@shared/models/group"
import { handleError } from "cc-frontend/error-handling"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import type Dialog from "cc-frontend/services/dialog"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import { isGroupMembershipGroup } from "cc-frontend/utils/group"
import { use } from "ember-could-get-used-to-this"
import { sortBy, toLower } from "lodash-es"
import * as R from "remeda" // tree-shaking supported!
import { tracked } from "tracked-built-ins"

interface Signature {
  Args: {
    group: Group
  }
}

export default class SchoolEditTeams extends Component<Signature> {
  @service declare dialog: Dialog
  @service declare modalManager: ModalManager
  @service declare rpc: Rpc

  @use groups = new FindManyAltogether(() => ["group", this.groupIds])

  @tracked isCreatingTeam = false
  @tracked nameOfNewTeam = null

  get teamMemberships() {
    return pipe(
      get(this.args.group.attributes, "memberships"),
      A.filter(isGroupMembershipGroup),
      A.filter((membership) => membership.attributes.usedAs === "team")
    )
  }

  get groupIds() {
    return R.pipe(
      get(this.args.group.attributes, "memberships"),
      R.filter(isGroupMembershipGroup),
      R.map((membership) => membership.attributes.groupId)
    )
  }

  get sortedGroups() {
    return sortBy(this.groups.content, (group) => toLower(group.attributes.title))
  }

  @action
  addTeam() {
    this.dialog
      .blank("dialogs/ask-for-team-title", {
        className: "bold-dialog",
        acceptHandler: "teamTitleAcceptHandler",
        nameOfNewTeam: null,
      })
      .then((nameOfNewTeam: string) => {
        this.isCreatingTeam = true
        this.rpc
          .group_create_team({
            groupId: this.args.group.id,
            title: nameOfNewTeam,
          })
          .then((response) => {
            if (response.responseOk) {
              this.isCreatingTeam = false
              this.nameOfNewTeam = null
            }
          })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "School::Edit::Teams": typeof SchoolEditTeams
  }
}
